import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken, formatApiError } from "@/lib/api";
import { Navigate, useLocation } from "react-router-dom";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    // null = checking, false = anonymous, object = user
    const [user, setUser] = useState(null);

    const refresh = useCallback(async () => {
        try {
            const { data } = await api.get("/auth/me");
            setUser(data);
        } catch {
            setUser(false);
        }
    }, []);

    useEffect(() => {
        if (getToken()) {
            refresh();
        } else {
            setUser(false);
        }
    }, [refresh]);

    const login = useCallback(async (email, password) => {
        try {
            const { data } = await api.post("/auth/login", { email, password });
            if (data.token) setToken(data.token);
            setUser({ id: data.id, email: data.email, role: data.role });
            return { ok: true };
        } catch (e) {
            return { ok: false, error: formatApiError(e) };
        }
    }, []);

    const logout = useCallback(async () => {
        try {
            await api.post("/auth/logout");
        } catch {
            // ignore
        }
        setToken(null);
        setUser(false);
    }, []);

    return (
        <AuthContext.Provider value={{ user, login, logout, refresh }}>
            {children}
        </AuthContext.Provider>
    );
}

export function useAuth() {
    const ctx = useContext(AuthContext);
    if (!ctx) throw new Error("useAuth must be used within AuthProvider");
    return ctx;
}

export function ProtectedRoute({ children }) {
    const { user } = useAuth();
    const location = useLocation();
    if (user === null) {
        return (
            <div className="flex h-screen items-center justify-center">
                <div className="h-8 w-8 rounded-full border-2 border-brand border-t-transparent animate-spin" />
            </div>
        );
    }
    if (!user) {
        return <Navigate to="/login" replace state={{ from: location }} />;
    }
    return children;
}
